/**
 * TaskRelationshipsModal - Modal de edição rápida dos relacionamentos
 * 
 * Permite vincular/desvincular Contato, Negócio e Tarefa Pai
 * diretamente da sidebar do modal de tarefa.
 * 
 * Regras de interdependência:
 * - Ao selecionar negócio → auto-preenche contato (se deal tiver contact_id)
 * - Ao selecionar contato → filtra negócios pelo contato selecionado
 * 
 * @version 2.0.0
 * RELEASE: 2026-04-07 v1.0.0 - Criação inicial
 * RELEASE: 2026-04-07 v2.0.0 - Interdependência contato↔negócio
 */
import { useState, useEffect } from 'react';
import { Loader2, Link2 } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { ContactSelector } from '@/components/crm/ContactSelector';
import { DealSelector } from '@/components/tasks/DealSelector';
import { ParentTaskSelector } from '@/components/tasks/ParentTaskSelector';
import type { TaskDetail } from '@/hooks/useTaskDetails';

interface TaskRelationshipsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: TaskDetail;
  /** Função para salvar os relacionamentos */
  onSave: (params: Record<string, any>) => Promise<void>;
  isSaving?: boolean;
}

/**
 * Modal de edição dos vínculos de relacionamento
 */
export function TaskRelationshipsModal({
  open,
  onOpenChange,
  task,
  onSave,
  isSaving = false,
}: TaskRelationshipsModalProps) {
  const isMobile = useIsMobile();

  // Estados locais dos seletores
  const [contactId, setContactId] = useState<string | null>(null);
  const [dealId, setDealId] = useState<string | null>(null);
  const [parentTaskId, setParentTaskId] = useState<string | null>(null);

  // Sincroniza estado local ao abrir
  useEffect(() => {
    if (open) {
      setContactId(task.contact_id || null);
      setDealId(task.deal_id || null);
      setParentTaskId(task.parent_task_id || null);
    }
  }, [open, task]);

  /**
   * Callback do DealSelector: auto-preenche contato ao selecionar negócio
   * Se o deal tem contact_id, atualiza o campo contato automaticamente
   */
  const handleDealData = (deal: { id: string; contact_id: string | null } | null) => {
    if (deal?.contact_id) {
      setContactId(deal.contact_id);
    }
  };

  // Monta parâmetros de atualização (somente o que mudou)
  const handleSave = async () => {
    const params: Record<string, any> = {};

    // Contato
    if (contactId !== (task.contact_id || null)) {
      if (contactId) {
        params.p_contact_id = contactId;
      } else {
        params.p_clear_contact = true;
      }
    }

    // Negócio
    if (dealId !== (task.deal_id || null)) {
      if (dealId) {
        params.p_deal_id = dealId;
      } else {
        params.p_clear_deal = true;
      }
    }

    // Tarefa pai
    if (parentTaskId !== (task.parent_task_id || null)) {
      if (parentTaskId) {
        params.p_parent_task_id = parentTaskId;
      } else {
        params.p_clear_parent_task = true;
      }
    }

    // Se nada mudou, apenas fecha
    if (Object.keys(params).length === 0) {
      onOpenChange(false);
      return;
    }

    await onSave(params);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={
          isMobile
            ? 'w-screen h-[100dvh] max-w-none rounded-none border-0 p-4 flex flex-col'
            : 'sm:max-w-[620px]'
        }
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-primary" />
            Relacionamentos
          </DialogTitle>
        </DialogHeader>

        {/* Conteúdo scrollável no mobile */}
        <div className={`space-y-4 py-2 ${isMobile ? 'flex-1 overflow-y-auto' : ''}`}>
          {/* Cliente/Contato */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Cliente / Contato</Label>
            <ContactSelector
              value={contactId}
              onSelect={setContactId}
              placeholder="Selecionar contato..."
            />
          </div>

          {/* Negócio — filtra por contato selecionado */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Negócio</Label>
            <DealSelector
              value={dealId}
              onSelect={setDealId}
              contactId={contactId}
              onDealData={handleDealData}
              placeholder="Selecionar negócio..."
            />
          </div>

          {/* Tarefa Pai */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Tarefa Pai</Label>
            <ParentTaskSelector
              value={parentTaskId}
              onSelect={setParentTaskId}
              excludeTaskId={task.id}
              placeholder="Selecionar tarefa pai..."
            />
          </div>
        </div>

        {/* Footer sticky no mobile */}
        <DialogFooter className={isMobile ? 'sticky bottom-0 bg-background pt-2 border-t' : ''}>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
